/** PROTOTYPE Anchors tab: a flat, excel-dense, two-panel list — no gantt.
 *  One row per OUTSIDE activity (always alone: status, name, a tiny work-code
 *  pill when it has one, P6 code, dates). To the RIGHT, the multiple
 *  in-section activities it ties to, as editable work-color pills (retype /
 *  lag / remove) — a new tie just gets added there. Left panel = INCOMING
 *  (outside feeds the in-section work), right panel = OUTGOING (outside waits
 *  on it). Severed deletes stay as struck-through pills. One review ✓ per
 *  outside row covers all its ties. Drag the center divider to resize; each
 *  panel scrolls on its own. */
import React from "react";
import type { DepGraph, DepGraphActivity, DepGraphEdge } from "../../../models";
import { predTypeFromShort, shortFromPredType } from "../../../models";
import { hashColor } from "../PullPlanGrid/helpers";
import ActivityLine from "../depCore/ActivityLine/ActivityLine";
import DepChip from "../depCore/DepChip/DepChip";
import DepChipTypeahead, { type DepCandidate } from "../depCore/DepChipTypeahead/DepChipTypeahead";
import { buildCandidates } from "../depCore/candidates";
import { type DepEditIntents } from "../depCore/DepTable/DepTable";
import { activityLabel, anchorKeyOf, isDriver, nodeStatus } from "../depCore/graphRows";
import { fmtNumericDay } from "../utils/dateFormatters";
import "./AnchorsBoard.css";

interface Props {
  graph: DepGraph | null;
  loading: boolean;
  error: string | null;
  readOnly: boolean;
  intents: DepEditIntents;
  reviewedAnchors?: Set<string>;
  onToggleAnchorReview?: (key: string) => void;
  /** Return to the board (the Anchors view otherwise has no obvious exit). */
  onBack?: () => void;
}

interface Tie {
  key: string;
  scoped: DepGraphActivity;
  predId: string;
  succId: string;
  type: DepGraphEdge["type"];
  lagHours: number;
}

interface ExtRow {
  ext: DepGraphActivity;
  ties: Tie[];
  /** Severed ties (their in-section end is a staged delete): struck names.
   *  `wasDriving` flags the ties that were binding dates against the deleted
   *  work — surfaced first, with a badge. */
  severed: Array<{ key: string; name: string; wasDriving: boolean }>;
}

/** Work-code color with the board's deterministic fallback. */
function pillColor(a: DepGraphActivity): string {
  return a.color || (a.work_code ? hashColor(a.work_code) : "");
}

const AnchorsBoard: React.FC<Props> = ({
  graph: graphProp,
  loading,
  error,
  readOnly,
  intents,
  reviewedAnchors,
  onToggleAnchorReview,
  onBack,
}) => {
  // Keep the last good graph so a transient fetch error (e.g. a stale staged
  // edit) shows a banner over the previous board instead of blanking it.
  const lastGraphRef = React.useRef<DepGraph | null>(null);
  if (graphProp && Object.keys(graphProp.activities).length > 0) {
    lastGraphRef.current = graphProp;
  }
  const graph = graphProp ?? lastGraphRef.current;
  const [adding, setAdding] = React.useState<string | null>(null); // dir+ext id
  const addRef = React.useRef<HTMLButtonElement | null>(null);
  // Center divider: % width of the LEFT panel; drag to give either side room.
  const [leftPct, setLeftPct] = React.useState(50);
  const boardRef = React.useRef<HTMLDivElement | null>(null);
  const startDivider = (e: React.MouseEvent) => {
    e.preventDefault();
    const root = boardRef.current;
    if (!root) return;
    const rect = root.getBoundingClientRect();
    const onMove = (ev: MouseEvent) => {
      const pct = ((ev.clientX - rect.left) / rect.width) * 100;
      setLeftPct(Math.max(12, Math.min(88, pct)));
    };
    const onUp = () => {
      window.removeEventListener("mousemove", onMove);
      window.removeEventListener("mouseup", onUp);
    };
    window.addEventListener("mousemove", onMove);
    window.addEventListener("mouseup", onUp);
  };

  const { incoming, outgoing, inCandidates } = React.useMemo(() => {
    const inc = new Map<string, ExtRow>();
    const out = new Map<string, ExtRow>();
    if (!graph) {
      return { incoming: [], outgoing: [], inCandidates: [] as DepCandidate[] };
    }
    const rowFor = (m: Map<string, ExtRow>, ext: DepGraphActivity): ExtRow => {
      let r = m.get(ext.id);
      if (!r) {
        r = { ext, ties: [], severed: [] };
        m.set(ext.id, r);
      }
      return r;
    };
    for (const [succId, edges] of Object.entries(graph.edges)) {
      const succ = graph.activities[succId];
      if (!succ) continue;
      for (const e of edges) {
        const pred = graph.activities[e.pred_id];
        if (!pred) continue;
        const predDriver = isDriver(pred);
        if (predDriver === isDriver(succ)) continue;
        const tie: Tie = {
          key: anchorKeyOf(e.pred_id, succId),
          scoped: predDriver ? succ : pred,
          predId: e.pred_id,
          succId,
          type: e.type,
          lagHours: e.lag_hours,
        };
        if (predDriver) rowFor(inc, pred).ties.push(tie);
        else rowFor(out, succ).ties.push(tie);
      }
    }
    for (const s of graph.severed_anchors ?? []) {
      const ext = graph.activities[s.external_id];
      if (!ext) continue;
      rowFor(s.direction === "in" ? inc : out, ext).severed.push({
        key: anchorKeyOf(s.pred_id, s.succ_id),
        name: s.deleted_name || s.deleted_task_code || s.deleted_id,
        wasDriving: !!s.was_driving,
      });
    }
    const sortRows = (m: Map<string, ExtRow>) =>
      Array.from(m.values())
        .map((r) => ({
          ...r,
          ties: [...r.ties].sort(
            (a, b) =>
              (a.scoped.start ?? "").localeCompare(b.scoped.start ?? "") ||
              a.scoped.name.localeCompare(b.scoped.name),
          ),
          // Driving (load-bearing) severed ties first.
          severed: [...r.severed].sort(
            (a, b) => Number(b.wasDriving) - Number(a.wasDriving),
          ),
        }))
        .sort(
          (a, b) =>
            // Rows that lost a DRIVING tie float to the top — re-home those first.
            Number(b.severed.some((s) => s.wasDriving)) -
              Number(a.severed.some((s) => s.wasDriving)) ||
            (a.ext.start ?? "").localeCompare(b.ext.start ?? "") ||
            a.ext.name.localeCompare(b.ext.name),
        );
    return {
      incoming: sortRows(inc),
      outgoing: sortRows(out),
      // ＋ picks an IN-SECTION activity to tie to that outside row.
      inCandidates: buildCandidates(Object.values(graph.activities), graph.link_candidates ?? []),
    };
  }, [graph]);

  const rowKeys = (r: ExtRow) => [
    ...r.ties.map((t) => t.key),
    ...r.severed.map((s) => s.key),
  ];

  const extCell = (a: DepGraphActivity, dir: "in" | "out") => (
    <div className="anchors-ext">
      <ActivityLine
        workCode={a.work_code}
        name={activityLabel(a)}
        fullName={a.name}
        taskCode={a.task_code}
        color={a.work_code ? pillColor(a) : ""}
        status={nodeStatus(a)}
        marker={dir}
      />
    </div>
  );

  const tieChips = (r: ExtRow, dir: "in" | "out") => {
    const addKey = `${dir}:${r.ext.id}`;
    return (
      <div className="anchors-ties">
        {r.ties.map((t) => (
          <span
            className={"anchors-tie-item" + (dir === "out" ? " anchors-tie-item--flip" : "")}
            key={t.key}
          >
            {dir === "in" && (
              <>
                <span className="anchors-d anchors-d--mid">{fmtNumericDay(t.scoped.start)}</span>
                <span className="anchors-d anchors-d--mid">{fmtNumericDay(t.scoped.finish)}</span>
              </>
            )}
            {readOnly ? (
              <ActivityLine
                dense
                workCode={t.scoped.work_code}
                name={activityLabel(t.scoped)}
                fullName={t.scoped.name}
                color={pillColor(t.scoped)}
                rel={{ type: t.type, lagHours: t.lagHours }}
              />
            ) : (
              <DepChip
                workCode={t.scoped.work_code || "—"}
                workName={activityLabel(t.scoped)}
                fullName={t.scoped.name}
                taskCode={t.scoped.task_code}
                zoneLabel={null}
                color={pillColor(t.scoped)}
                predType={predTypeFromShort(t.type)}
                lagHours={t.lagHours}
                onChangeRel={(type, days) =>
                  intents.onRetypeEdge(t.succId, t.predId, shortFromPredType(type), days)
                }
                onDelete={() => intents.onRemoveEdge(t.succId, t.predId)}
              />
            )}
            {dir === "out" && (
              <>
                <span className="anchors-d anchors-d--mid">{fmtNumericDay(t.scoped.start)}</span>
                <span className="anchors-d anchors-d--mid">{fmtNumericDay(t.scoped.finish)}</span>
              </>
            )}
          </span>
        ))}
        {r.severed.map((s) => (
          <span
            className={"anchors-tie-item" + (dir === "out" ? " anchors-tie-item--flip" : "")}
            key={s.key}
          >
            {dir === "in" && (
              <>
                <span className="anchors-d anchors-d--mid">—</span>
                <span className="anchors-d anchors-d--mid">—</span>
              </>
            )}
            <ActivityLine
              dense
              severed
              workCode=""
              name={s.name}
              color=""
            />
            {s.wasDriving && (
              <span
                className="anchors-driving-badge"
                title={
                  dir === "in"
                    ? "This outside activity was DRIVING the date of the deleted work — re-tie it so the new plan stays anchored."
                    : "The deleted work was DRIVING this outside activity's date — re-tie it or it floats free."
                }
              >
                ⚡ was driving
              </span>
            )}
            {dir === "out" && (
              <>
                <span className="anchors-d anchors-d--mid">—</span>
                <span className="anchors-d anchors-d--mid">—</span>
              </>
            )}
          </span>
        ))}
        {!readOnly && (
          <span className="anchors-tie-item anchors-tie-item--add">
            {dir === "in" && (
              <>
                <span className="anchors-d anchors-d--mid" />
                <span className="anchors-d anchors-d--mid" />
              </>
            )}
            <span className="anchors-add-wrap">
            <button
              type="button"
              ref={adding === addKey ? addRef : undefined}
              className="anchors-add"
              title={
                dir === "in"
                  ? "Tie another in-section activity to this anchor (as its successor)"
                  : "Tie another in-section activity to this anchor (as its pred)"
              }
              onClick={() => setAdding((k) => (k === addKey ? null : addKey))}
            >
              +
            </button>
            {adding === addKey && (
              <DepChipTypeahead
                candidates={inCandidates}
                anchorRef={addRef}
                onPick={(c) => {
                  if (dir === "in") intents.onAddEdge(r.ext.id, c.instanceId);
                  else intents.onAddEdge(c.instanceId, r.ext.id);
                  setAdding(null);
                }}
                onClose={() => setAdding(null)}
              />
            )}
            </span>
          </span>
        )}
      </div>
    );
  };

  const review = (r: ExtRow) => {
    // Read-only (e.g. the publish review) has no review tracking, but an empty
    // cell must still occupy grid column 1 or every row's columns shift left.
    if (!onToggleAnchorReview) return <span aria-hidden="true" />;
    const keys = rowKeys(r);
    const done = keys.length > 0 && keys.every((k) => reviewedAnchors?.has(k));
    return (
      <input
        type="checkbox"
        className="anchors-review"
        checked={done}
        title="Reviewed — this anchor's connections work for the new plan"
        onChange={() => {
          const flips = done
            ? keys
            : keys.filter((k) => !(reviewedAnchors?.has(k) ?? false));
          for (const k of flips) onToggleAnchorReview(k);
        }}
      />
    );
  };

  const panel = (
    dir: "in" | "out",
    title: string,
    rows: ExtRow[],
  ) => (
    <div className="anchors-panel">
      {dir === "in" ? (
        <div className="anchors-panel-cols">
          <span />
          <span className="anchors-cols-title">
            {title} <span className="anchors-panel-count">{rows.length}</span>
          </span>
          <span className="anchors-d anchors-d--end">Start</span>
          <span className="anchors-d anchors-d--end">Finish</span>
          <span />
          <span className="anchors-cols-right">
            <span className="anchors-d anchors-d--mid">Start</span>
            <span className="anchors-d anchors-d--mid">Finish</span>
            <span className="anchors-cols-label">In section</span>
          </span>
        </div>
      ) : (
        <div className="anchors-panel-cols anchors-panel-cols--flip">
          <span />
          <span className="anchors-cols-title anchors-cols-title--ties">
            <span className="anchors-cols-label">In section</span>
            <span className="anchors-cols-spacer" />
            <span className="anchors-d anchors-d--mid">Start</span>
            <span className="anchors-d anchors-d--mid">Finish</span>
          </span>
          <span />
          <span className="anchors-d anchors-d--start">Start</span>
          <span className="anchors-d anchors-d--start">Finish</span>
          <span className="anchors-cols-title">
            {title} <span className="anchors-panel-count">{rows.length}</span>
          </span>
        </div>
      )}
      <div className="anchors-panel-scroll">
        {rows.map((r) => {
          const keys = rowKeys(r);
          const done = keys.length > 0 && keys.every((k) => reviewedAnchors?.has(k));
          return (
            <div
              key={r.ext.id}
              className={
                "anchors-row" +
                (dir === "out" ? " anchors-row--flip" : "") +
                (done ? " anchors-row--reviewed" : "") +
                (r.severed.length > 0 ? " anchors-row--severed" : "") +
                // Every tie cut and the new plan re-tied none — this outside
                // activity now has NO connection to the plan. Flag it red.
                (r.ties.length === 0 && r.severed.length > 0
                  ? " anchors-row--orphaned"
                  : "")
              }
            >
              {review(r)}
              {dir === "in" ? (
                <>
                  {extCell(r.ext, dir)}
                  <span className="anchors-d anchors-d--end">{fmtNumericDay(r.ext.start)}</span>
                  <span className="anchors-d anchors-d--end">{fmtNumericDay(r.ext.finish)}</span>
                  <span className="anchors-arrow">→</span>
                  {tieChips(r, dir)}
                </>
              ) : (
                <>
                  {tieChips(r, dir)}
                  <span className="anchors-arrow">→</span>
                  <span className="anchors-d anchors-d--start">{fmtNumericDay(r.ext.start)}</span>
                  <span className="anchors-d anchors-d--start">{fmtNumericDay(r.ext.finish)}</span>
                  {extCell(r.ext, dir)}
                </>
              )}
            </div>
          );
        })}
        {rows.length === 0 && <div className="anchors-empty">None</div>}
      </div>
    </div>
  );

  if (loading && !graph) return <div className="anchors-empty">Loading anchors…</div>;
  if (error && !graph) return <div className="anchors-empty">{error}</div>;

  const drivingCount = [...incoming, ...outgoing].reduce(
    (n, r) => n + r.severed.filter((s) => s.wasDriving).length,
    0,
  );

  return (
    <div className="anchors-board-wrap">
      {onBack && (
        <div className="anchors-backbar">
          <button type="button" className="anchors-back" onClick={onBack}>
            ← Back to board
          </button>
        </div>
      )}
      {drivingCount > 0 && (
        <div className="anchors-driving-banner">
          ⚡ <b>{drivingCount} driving tie{drivingCount === 1 ? "" : "s"}</b>{" "}
          severed by this re-pull — the deleted work was setting these dates.
          Re-tie these first; they're flagged <b>⚡ was driving</b> and sorted to
          the top.
        </div>
      )}
      {error && (
        <div className="anchors-error-banner">
          Your last dependency edit couldn't be applied — showing the previous
          view. Undo it from the history panel.
        </div>
      )}
      {graph?.cycle_error && (
        <div className="anchors-cycle-warn">
          <b>Dependency loop detected</b> — the schedule pass can't run; dates
          shown are stored values. Break the loop by removing one of these
          ties:{" "}
          {(graph.cycle_activities ?? [])
            .map((c) => `${c.task_code ? c.task_code + " · " : ""}${c.name}`)
            .join(" → ") || "(loop members unavailable)"}
        </div>
      )}
    <div className="anchors-board" ref={boardRef}>
      <div className="anchors-side" style={{ width: `calc(${leftPct}% - 5px)` }}>
        {panel("in", "Incoming", incoming)}
      </div>
      <div
        className="anchors-divider"
        title="Drag to resize the panels"
        onMouseDown={startDivider}
      />
      <div className="anchors-side" style={{ width: `calc(${100 - leftPct}% - 5px)` }}>
        {panel("out", "Outgoing", outgoing)}
      </div>
    </div>
    </div>
  );
};

export default AnchorsBoard;
